import { safeLocalStorage } from '@/lib/safeStorage';
import React, {createContext, useContext, useEffect, useState} from 'react';

type Theme = 'light' | 'dark';

interface ThemeContextProps {
  theme: Theme;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextProps | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: React.ReactNode;
  forceLight?: boolean;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children, forceLight = false }) => {
  const [theme, setTheme] = useState<Theme>(() => {
    // If forceLight is true, always return light theme
    if (forceLight) {
      return 'light';
    }
    
    // Check localStorage for saved theme preference
    const savedTheme = safeLocalStorage.getItem('theme') as Theme;
    if (savedTheme && (savedTheme === 'light' || savedTheme === 'dark')) {
      return savedTheme;
    }
    
    // Check system preference
    if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
    
    return 'light';
  });

  // Handle forceLight prop changes
  useEffect(() => {
    if (forceLight && theme !== 'light') {
      setTheme('light');
    }
  }, [forceLight]);

  useEffect(() => {
    const root = document.documentElement;
    
    console.log('Theme context: applying theme', theme);
    
    // Remove existing theme classes
    root.classList.remove('light', 'dark');
    
    // Add current theme class
    root.classList.add(theme);
    
    console.log('HTML element classes after theme change:', root.classList.toString());
    console.log('CSS variables check:', {
      background: getComputedStyle(root).getPropertyValue('--background'),
      foreground: getComputedStyle(root).getPropertyValue('--foreground')
    });
    
    // Save to localStorage
    safeLocalStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    if (forceLight) {
      // Don't allow theme toggling when forceLight is true
      return;
    }
    setTheme(prevTheme => prevTheme === 'light' ? 'dark' : 'light');
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};